import React from 'react';
import { toast } from 'sonner';

function getSafeAlertMessage(message: unknown): string | JSX.Element {
  if (React.isValidElement(message)) return message as JSX.Element;
  if (typeof message === 'string') return message;
  if (message instanceof Error) return message.message;

  if (message && typeof message === 'object') {
    const maybeDetail = (message as any).detail;
    if (typeof maybeDetail === 'string' && maybeDetail.trim()) return maybeDetail;

    const maybeMessage = (message as any).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim()) return maybeMessage;

    const maybeErrors = (message as any).errors;
    if (Array.isArray(maybeErrors) && maybeErrors.length > 0) {
      const first = maybeErrors[0];
      if (first && typeof first === 'object') {
        const { name } = (first as any);
        const msg = (first as any).message;
        if (typeof name === 'string' && name.trim()) return name;
        if (typeof msg === 'string' && msg.trim()) return msg;
      }
      if (typeof maybeErrors[0] === 'string' && maybeErrors[0].trim()) return maybeErrors[0];
    }

    try {
      return JSON.stringify(message);
    } catch {
      // fall through
    }
  }

  return String(message ?? '');
}

const createAlert = (
  type: 'success' | 'warning' | 'error',
) => (message: string | Error | JSX.Element) => {
  const messageText = getSafeAlertMessage(message);

  return toast[type](messageText, {
    duration: 3000,
  });
};

export const success = createAlert('success');
export const warning = createAlert('warning');
export const error = createAlert('error');

type StatusStrings = {
  success?: string,
  warning?: string,
  error?: string,
  [key: number]: string,
};

export const forStatus = (status: number, strings: StatusStrings) => {
  // Error occured but not on network level, so treat it as an error
  if (!status && strings.error) error(strings.error);

  // Overwrites any of the main status codes
  if (strings[status]) {
    warning(strings[status]);

    return true;
  }

  if (strings.success && status >= 200 && status <= 299) success(strings.success);
  if (strings.warning && status >= 400 && status <= 499) warning(strings.warning);
  if (strings.error && status >= 500) error(strings.error);

  return true;
};

export const AlertService = { success, warning, error, forStatus };
